import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Stack} from '@mui/material';
import {finalize} from 'rxjs';
import {useSnackbar} from 'notistack';
import {GridCellParams, GridRowParams, GridValueGetterParams} from '@mui/x-data-grid-pro';

import {ClientsService, clientsServicesService} from '@store/clients/services';

import ModalComponent from '@components/modal/Modal.component';
import NewTableComponent from '@components/table/NewTable.component';

import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {useNavigate} from 'react-router-dom';
import CheckboxComponent from '@components/inputs/checkbox/Checkbox.component';
import CreateServiceDrawer from '@screens/admin/clients/details/contractedServices/manageService/CreateService.drawer';
import {Add} from '@icons/index';
import {ClientsContract} from '@store/clients/contracts';
import dayjs from 'dayjs';

interface ContractServicesModalProps {
  contractId: string | number;
  contract?: ClientsContract;
  handleClose: () => void;
  showAddServiceButton?: boolean;
}

const ContractServicesModal = (props: ContractServicesModalProps) => {
  const {contractId, handleClose, contract, showAddServiceButton = true} = props;

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [filterExpiredServices, setFilterExpiredServices] = useState(true);

  const [showAddService, setShowAddService] = useState(false);

  const [contractServices, setContractServices] = useState<ClientsService[]>([]);

  useEffect(() => {
    if (contractId) {
      setLoading(true);
      clientsServicesService
        .searchClientsServices('', [contractId], filterExpiredServices)
        .pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: (sp) => setContractServices(sp.data),
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    }
  }, [contractId, enqueueSnackbar, filterExpiredServices]);

  return (
    <ModalComponent
      aria-label="over-drawer"
      handleClose={handleClose}
      maxWidth={false}
      content={
        <Stack pt="20px" height="65vh" width="100%" spacing={2} overflow="hidden">
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <CheckboxComponent
              label={t('clients.details.contractedServices.manage.selectContract.table.filterExpiredServices')}
              value={filterExpiredServices}
              handleChange={setFilterExpiredServices}
            />
            {showAddServiceButton && <Button
                variant="contained" color="primary"
                onClick={() => setShowAddService(true)}
                startIcon={<Add/>}>
              {t('clients.addButtons.service')}
            </Button>}
          </Stack>
          <NewTableComponent
            loading={loading}
            columns={[
              COLUMN_TYPES.TEXT_BOLD({field: 'id', label: t('clients.servicesTable.id'), width: 100}),
              COLUMN_TYPES.TEXT_BOLD({
                field: 'service.clientWasteName',
                label: t('clients.servicesTable.name'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.clientWasteName,
                width: 350,
              }),
              COLUMN_TYPES.TEXT({
                field: 'createdAt',
                label: t('clients.servicesTable.creationDate'),
                valueGetter: (params: GridValueGetterParams<ClientsService>) => params.value ? dayjs(params.value).format('DD.MM.YYYY') : '-',
                minWidth: 130,
              }),
              COLUMN_TYPES.TEXT({
                field: 'schedulesCount',
                label: t('clients.servicesTable.frequency'),
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) =>
                  (params.value ?? 0) > 0 ? t('global.frequencies.recurrent') : t('global.frequencies.onDemand'),
                width: 130,
              }),
              COLUMN_TYPES.TEXT({
                field: 'kind.kind',
                label: t('clients.servicesTable.type'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.kindName,
                width: 250,
              }),
              COLUMN_TYPES.TEXT({
                field: 'waste.name',
                label: t('clients.servicesTable.details'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.wasteName,
                width: 250,
              }),
              COLUMN_TYPES.TEXT({
                field: 'container.name',
                label: t('clients.servicesTable.container'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.containerName,
                width: 200,
              }),
              COLUMN_TYPES.TEXT({
                field: 'sOrgUnit.name',
                label: t('clients.servicesTable.supplier'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) =>
                  params.row.supplierOrganisationUnitName,
                width: 350,
              }),
              COLUMN_TYPES.TEXT({
                field: 'cOrgUnit.name',
                label: t('clients.servicesTable.clientUnit'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) =>
                  params.row.clientOrganisationUnitName,
                width: 350,
              }),
              COLUMN_TYPES.TEXT({
                field: 'buyPriceUnit',
                label: t('clients.servicesTable.purchasePrice'),
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) =>
                  params.row.buyPriceIfOnlyOne !== null
                    ? `${params.row.buyPriceIfOnlyOne / 100} ${params.row.buyPriceCurrency || ''}`
                    : `${params.row.buyPricesCount || '-'}${
                      params.row.buyPricesCount ? ' ' + t('suppliers.details.contractedServices.table.prices') : ''
                    }`,
                width: 130,
              }),
              COLUMN_TYPES.TEXT({
                field: 'sellPriceUnit',
                label: t('clients.servicesTable.sellingPrice'),
                cellClassName: (params: GridCellParams<ClientsService>) =>
                  params.row.buyPriceIfOnlyOne &&
                  params.row.sellPriceIfOnlyOne &&
                  params.row.buyPriceIfOnlyOne > params.row.sellPriceIfOnlyOne
                    ? 'error'
                    : '',
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) =>
                  params.row.sellPriceIfOnlyOne !== null
                    ? `${params.row.sellPriceIfOnlyOne / 100} ${params.row.sellPriceCurrency || ''}`
                    : `${params.row.sellPricesCount || '-'}${
                      params.row.sellPricesCount ? ' ' + t('suppliers.details.contractedServices.table.prices') : ''
                    }`,
                width: 130,
              }),
              COLUMN_TYPES.ACTIONS({
                handleShowRow: (clientsService: ClientsService) => navigate(`/clients/contracts/services/${clientsService.id}`),
              }),
            ]}
            getRowClassName={(params: GridRowParams) => {
              if (params.row.expired) {
                return 'grey-row';
              }
              return '';
            }}
            disableRowSelection
            paginationMode="client"
            disablePagination
            data={contractServices}
          />
          {showAddService && (
            <CreateServiceDrawer fromContract={contract} handleClose={() => setShowAddService(false)}/>)}
        </Stack>
      }
    />
  );
};

export default ContractServicesModal;
